import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import userManualDoctor from "../../helpers/documentDoctor.md";
import userManualItkan from "../../helpers/documentItkan.md"
import userManualQuality from "../../helpers/documentQuality.md"
import userManualMedicalLead from "../../helpers/documentMedicalLead.md"
import { Box, Typography, Link } from "@mui/material";
import { ENV, StorageHelper } from "../../helpers";
import { colors } from "../../theme/colors";
const Documentation = () => {
  const [content, setContent] = useState("");
  const [toc, setToc] = useState([]);
  const user = JSON.parse(StorageHelper.get("user")); // fetch user details from storage
  const [activeSection, setActiveSection] = useState("");
  useEffect(() => {
    // Fetch the Markdown file
    fetch(
      user?.role_id == ENV.doctor_role_id
        ? userManualDoctor
        : user?.role_id == ENV.quality_user_role_id
          ? userManualQuality
          : user?.role_id == ENV.itkan_role_id
            ? userManualItkan
            : user?.role_id == ENV.medical_lead_role_id
              ? userManualMedicalLead
              : userManualDoctor
    ) // Based on role document needs to change
      .then((response) => response.text())
      .then((text) => {
        setContent(text);

        // Extract Table of Contents dynamically
        const extractedToc = [];
        const lines = text.split("\n");
        const numbering = [];
        const slugCount = {};

        lines.forEach((line) => {
          const headingMatch = line.match(/^(#{1,6})\s+(.*)/);
          if (headingMatch) {
            const [, hashes, title] = headingMatch;
            const level = hashes.length;

            // Adjust numbering for hierarchy
            if (numbering.length < level) {
              numbering.push(1); // Add new level
            } else {
              numbering[level - 1]++; // Increment current level
              numbering.length = level; // Reset deeper levels
            }

            const numberString = numbering.join(".");
            let slug = title
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^a-z0-9-]/g, "");

            // Handle duplicate slugs
            if (slugCount[slug]) {
              slugCount[slug] += 1;
              slug = `${slug}-${slugCount[slug]}`;
            } else {
              slugCount[slug] = 1;
            }

            extractedToc.push({ title: `${numberString} ${title}`, level, slug });
          }
        });
        setToc(extractedToc);
      });


  }, []);

  useEffect(() => {

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // setActiveSection(entry.target.id);
            // Scroll the TOC to show the active section
            const tocItem = document.querySelector(`[data-slug="${entry.target.id}"]`);
            if (tocItem) {
              tocItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
          }
        });
      },
      { threshold: 1.0 }
    );

    document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      observer.observe(heading);
    });

    return () => observer.disconnect();
  }, [content]);

  const handleScrollToSection = (slug) => {
    const section = document.getElementById(slug);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden", borderRadius: "5px", elevation: 3, padding: "1px", boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)" }}>
      {/* Left Sidebar: Table of Contents */}
      <Box
        sx={{
          width: "20%",
          borderRight: "1px solid",
          borderColor: "divider",
          overflowY: "auto",
          p: 2,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
          },
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ color: colors.PRIMARY, fontWeight: "bold" }}
        >
          Contents
        </Typography>
        {toc.map(({ title, level, slug }) => (
          <Link
            key={slug}
            data-slug={slug}
            onClick={() => handleScrollToSection(slug)}
            sx={{
              display: "block",
              pl: (level - 1) * 2,
              py: 0.5,
              cursor: "pointer",
              color: activeSection === slug ? colors.SECONDARY : colors.PRIMARY,
              textDecoration: "none",
              fontSize:"13px",
              fontWeight:600,
              backgroundColor: activeSection === slug ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              "&:hover": {
                textDecoration: "underline",
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              },
            }}
          >
            {title}
          </Link>
        ))}
      </Box>

      {/* Right Content: Markdown Content */}
      <Box
        id="documentation"
        sx={{
          width: "80%",
          overflowY: "auto",
          textDecoration: "none",
          fontSize: "1 rem",
          scrollBehavior: "smooth",
          "& a": {
            color: colors.PRIMARY,
            textDecoration: "none",
            cursor: "default",
          },
          p: 3,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
          },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            mb: 1, // Reduce margin below headings
            color: colors.PRIMARY,
          },
          "html":{
            fontSize:"16px"
          },
          "& h1": {
            fontWeight: 800,
            fontSize: "2rem !important",
            
          },
          "& h2": {
            fontWeight: 600,
            fontSize: "1.5rem !important",
           
          },
          "& h3": {
            fontWeight: 400,
            fontSize: "1.15rem !important",
          },
          "& h4": {
            fontWeight: 200,
            fontSize: "1rem !important",
          },
          "& p, & ul, & ol": {
            fontSize: "0.8rem !important",
            lineHeight: 1.5,
          },
          "& img": {
            maxWidth: "100%",
            height: "auto",
            mb: 2,
            mt: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px",
          },
        }}
      >
        <ReactMarkdown
          children={content}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSlug, [rehypeAutolinkHeadings, { behavior: "wrap" }]]}
        />
      </Box>
    </Box>
  );
};

export default Documentation;
